import type { SaleProfile } from '../../sale/model/profile'

/**
 * Настройки продажи и «строки для продажи» собственной кассы (служба Resource.Own: `api/own/v1/resources/{rid}/sale-settings` и
 * `…/sale-lines`). Разбор на границе (D-056): лишние
 * поля не мешают, неизвестное значение состояния читается как «недоступно» (сомнение не открывает продажу), а не как «есть».
 * Профиль строки (RP-OWN4, CASH-CONTRACTS §2) разбирается строго: значение не того типа или вне диапазона не подставляется и не
 * округляется, строка из-за него блокируется с причиной.
 */
export interface OwnSaleSettings {
  resourceId: string
  /** Вид цены по умолчанию (идентификатор вида цены ресурса); `null` — кассир выбирает сам. */
  defaultPriceTypeKey: string | null
}

export type OwnVatState = 'set' | 'missing' | 'unmapped' | 'unavailable'

/** Почему строку нельзя продавать, по слову службы; неизвестное слово не пропадает, а остаётся причиной. */
export type OwnLineProblem = 'product-not-found' | 'price-missing' | (string & {})

/** Состояние строки по слову службы: `ready` годится в продажу, `blocked` — нет. */
export type OwnLineState = 'ready' | 'blocked'

export interface OwnSaleLineItem {
  /** Ключ карточки собственной номенклатуры, как его прислал поиск для продажи. */
  cardKey: string
  name: string
  sku: string | null
  barcode: string | null
  unit: string | null
  /** Весовой ли товар (из профиля карточки); `null` — неизвестно. */
  isWeight: boolean | null
  /** Цена выбранного вида цены в копейках; `null` — в прайсе её нет (не 0). */
  priceKopecks: number | null
  /** Код ставки для кассового агента (`vat22`, `none`…); `null` — не определена. */
  vatRateCode: string | null
  vatState: OwnVatState
  problem: OwnLineProblem | null
  /**
   * Кассовый профиль карточки: предмет и способ расчёта, маркировка, весовой признак, единица, версия карточки и источник.
   * `null` — служба профиля не прислала: строка не продаётся.
   */
  profile: SaleProfile | null
  /** Состояние строки по слову службы; `null` — служба его не прислала. */
  state: OwnLineState | null
  /** Причины блокировки по слову службы (`marked-not-supported`, `read-failed`…); неизвестные слова остаются как есть. */
  blockReasons: readonly string[]
  /** Готовый русский текст службы о блокировке строки (показывается дословно); `null` — не прислан. */
  blockMessage: string | null
  /** RP-SY5: какие кассовые поля карточки не определены (`vatRate`, `unitCode`…) при причине `cash-field-undefined`; пусто — все определены. */
  undefinedFields: readonly string[]
  /** Что в ответе не по договору (неизвестный код, не тот тип): строка из-за этого блокируется, значение не подставляется. */
  decodeIssues: readonly string[]
}

export interface OwnSaleLines {
  resourceId: string
  priceTypeKey: string
  items: readonly OwnSaleLineItem[]
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value)
const text = (value: unknown): string | null => (typeof value === 'string' && value.length > 0 ? value : null)
const fail = (what: string): never => {
  throw new Error(`Ответ сервера не по договору: ${what}`)
}

export function decodeOwnSaleSettings(raw: unknown): OwnSaleSettings {
  if (!isRecord(raw)) return fail('настройки продажи')
  const resourceId = text(raw.resourceId)
  if (resourceId === null) return fail('у настроек продажи нет resourceId')
  return { resourceId, defaultPriceTypeKey: text(raw.defaultPriceTypeKey) }
}

const VAT_STATES: readonly OwnVatState[] = ['set', 'missing', 'unmapped', 'unavailable']
const PROFILE_KEYS = ['paymentSubject', 'paymentMethod', 'isMarked', 'isWeighted', 'unitCode', 'unitName', 'cardVersion', 'origin'] as const
const MONEY_TEXT = /^(\d+)(?:\.(\d{1,2}))?$/

/** Деньги строкой договора (до двух знаков) в копейки без плавающей арифметики; иначе `null`. */
function kopecksOfText(value: unknown): number | null {
  if (typeof value !== 'string') return null
  const match = MONEY_TEXT.exec(value)
  if (match === null) return null
  const kopecks = Number(match[1]) * 100 + Number((match[2] ?? '').padEnd(2, '0') || '0')
  return Number.isSafeInteger(kopecks) ? kopecks : null
}

/**
 * Профиль строки (строго). Значение неверного типа или вне диапазона не подставляется и не округляется: поле остаётся `null`,
 * а в `issues` записывается, что именно не так, — строка из-за этого блокируется.
 */
function decodeProfile(entry: Record<string, unknown>, issues: string[]): SaleProfile | null {
  const nested = isRecord(entry.profile) ? entry.profile : null
  // Плоские поля допустимы как запасной вид: служба вправе положить профиль в саму строку.
  const source = nested ?? (PROFILE_KEYS.some((key) => key in entry) ? entry : null)
  if (source === null) return null
  const code = (key: string, max: number): number | null => {
    const value = source[key]
    if (value === null || value === undefined) return null
    if (typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= max) return value
    issues.push(`${key}-invalid`)
    return null
  }
  const flag = (key: string): boolean | null => {
    const value = source[key]
    if (value === null || value === undefined) return null
    if (typeof value === 'boolean') return value
    issues.push(`${key}-invalid`)
    return null
  }
  const version = source.cardVersion ?? entry.cardVersion
  let cardVersion: number | null = null
  if (typeof version === 'number' && Number.isSafeInteger(version) && version >= 0) cardVersion = version
  else if (version !== null && version !== undefined) issues.push('cardVersion-invalid')
  const originRaw = source.origin ?? entry.origin
  let origin: SaleProfile['origin'] = null
  if (isRecord(originRaw)) {
    const resourceId = text(originRaw.resourceId)
    const key = text(originRaw.key)
    if (resourceId !== null && key !== null && originRaw.kind === 'own') origin = { resourceId, kind: 'own', key }
    else issues.push('origin-invalid')
  } else if (originRaw !== null && originRaw !== undefined) issues.push('origin-invalid')
  return {
    paymentSubject: code('paymentSubject', 33),
    paymentMethod: code('paymentMethod', 7),
    isMarked: flag('isMarked'),
    isWeighted: flag('isWeighted') ?? (typeof entry.isWeight === 'boolean' ? entry.isWeight : null),
    unitCode: text(source.unitCode),
    unitName: text(source.unitName) ?? text(entry.unit),
    cardVersion,
    origin,
  }
}

export function decodeOwnSaleLines(raw: unknown): OwnSaleLines {
  if (!isRecord(raw) || !Array.isArray(raw.items)) return fail('строки для продажи')
  const resourceId = text(raw.resourceId)
  if (resourceId === null) return fail('у строк нет resourceId')
  return {
    resourceId,
    priceTypeKey: text(raw.priceTypeKey) ?? '',
    items: raw.items.map((entry): OwnSaleLineItem => {
      if (!isRecord(entry)) return fail('строка не объект')
      const cardKey = text(entry.cardKey)
      if (cardKey === null) return fail('у строки нет cardKey')
      // Карточки нет (удалена или в архиве): службе нечего назвать, строка остаётся с ключом и причиной.
      const name = text(entry.name) ?? ''
      const issues: string[] = []
      const profile = decodeProfile(entry, issues)
      const price = entry.priceKopecks
      // Цена — только целые неотрицательные копейки (или деньги строкой договора): дробное или отрицательное — нет цены, а не «почти цена».
      const priceKopecks = typeof price === 'number' && Number.isSafeInteger(price) && price >= 0 ? price : kopecksOfText(entry.price)
      const blockReasons = Array.isArray(entry.blockReasons) ? entry.blockReasons.filter((reason): reason is string => typeof reason === 'string') : []
      if (Array.isArray(entry.blockReasons) && blockReasons.length !== entry.blockReasons.length) issues.push('blockReasons-invalid')
      const undefinedFields = Array.isArray(entry.undefinedFields) ? entry.undefinedFields.filter((field): field is string => typeof field === 'string') : []
      if (Array.isArray(entry.undefinedFields) && undefinedFields.length !== entry.undefinedFields.length) issues.push('undefinedFields-invalid')
      let state: OwnLineState | null = null
      if (entry.state === 'ready' || entry.state === 'blocked') state = entry.state
      else if (entry.state !== undefined && entry.state !== null) {
        issues.push('state-invalid')
        state = 'blocked'
      }
      const profileVat = isRecord(entry.profile) ? entry.profile.vatRate : undefined
      // Плоская ставка строки главнее ставки профиля; код ставки не из словаря агента не превращается в «без НДС»: он остаётся как есть и режется проверкой `isVatCode`.
      const vatRateCode = text(entry.vatRateCode) ?? text(profileVat)
      // Слово о ставке неизвестно или не прислано — «недоступно»: сомнение не открывает продажу.
      const vatState: OwnVatState = VAT_STATES.find((candidate) => candidate === entry.vatState) ?? 'unavailable'
      return {
        cardKey,
        name,
        sku: text(entry.sku),
        barcode: text(entry.barcode),
        unit: profile?.unitName ?? text(entry.unit),
        isWeight: profile?.isWeighted ?? (typeof entry.isWeight === 'boolean' ? entry.isWeight : null),
        priceKopecks,
        vatRateCode,
        vatState,
        problem: text(entry.problem),
        profile,
        state,
        blockReasons,
        blockMessage: text(entry.blockMessage),
        undefinedFields,
        decodeIssues: issues,
      }
    }),
  }
}
